import React from 'react';
import { Store, Pencil, Lock } from 'lucide-react';
import { Lojista, TAMANHO_UL_DROPDOWN_OPTIONS } from './types';

type LojistasTableProps = {
  data: Lojista[];
  loading?: boolean;
  editedTamanhos: Map<string, string>;
  onTamanhoChange: (cnpj: string, newTamanho: string) => void;
};

function formatCnpj(cnpj: string): string {
  const digits = cnpj.replace(/\D/g, '').padStart(14, '0');
  return digits.replace(
    /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/,
    '$1.$2.$3/$4-$5'
  );
}

export const LojistasTable: React.FC<LojistasTableProps> = ({
  data,
  loading,
  editedTamanhos,
  onTamanhoChange,
}) => {
  if (loading) {
    return (
      <div className="p-8 flex justify-center">
        <div className="animate-pulse space-y-3 w-full">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="h-12 bg-gray-100 rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead>
          <tr className="bg-gray-50">
            <th
              scope="col"
              className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider"
            >
              <div className="flex items-center gap-1.5">
                <Lock className="w-3 h-3 text-gray-400" />
                CNPJ
              </div>
            </th>
            <th
              scope="col"
              className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider"
            >
              <div className="flex items-center gap-1.5">
                <Lock className="w-3 h-3 text-gray-400" />
                Razão Social
              </div>
            </th>
            <th
              scope="col"
              className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider hidden md:table-cell"
            >
              <div className="flex items-center gap-1.5">
                <Lock className="w-3 h-3 text-gray-400" />
                Nome Fantasia
              </div>
            </th>
            <th
              scope="col"
              className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider"
            >
              <div className="flex items-center gap-1.5">
                <Pencil className="w-3 h-3 text-blue-500" />
                Tamanho UL
              </div>
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {data.map((row) => {
            const isEdited = editedTamanhos.has(row.CNPJLojista);
            const currentValue = isEdited
              ? editedTamanhos.get(row.CNPJLojista)!
              : row.tamanho_ul;

            return (
              <tr
                key={row.CNPJLojista}
                className={`transition-colors ${
                  isEdited
                    ? 'bg-blue-50/50 hover:bg-blue-50'
                    : 'bg-white hover:bg-gray-50'
                }`}
              >
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="text-sm font-mono text-gray-900">
                    {formatCnpj(row.CNPJLojista)}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <span className="text-sm text-gray-900 line-clamp-1">
                    {row.RazaoSocial || '-'}
                  </span>
                </td>
                <td className="px-6 py-4 hidden md:table-cell">
                  <span className="text-sm text-gray-600 line-clamp-1">
                    {row.NomeFantasia || '-'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <select
                    value={currentValue}
                    onChange={(e) => onTamanhoChange(row.CNPJLojista, e.target.value)}
                    className={`w-full max-w-[220px] pl-3 pr-3 py-2 text-sm rounded-lg border transition-all cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500/30 ${
                      isEdited
                        ? 'bg-blue-50 border-blue-300 text-blue-800 font-medium'
                        : currentValue === 'Não Informado'
                          ? 'bg-white border-gray-200 text-gray-400 hover:border-gray-300'
                          : 'bg-white border-gray-200 text-gray-700 hover:border-gray-300'
                    }`}
                    aria-label={`Tamanho UL do lojista ${row.RazaoSocial || row.CNPJLojista}`}
                  >
                    {TAMANHO_UL_DROPDOWN_OPTIONS.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {data.length === 0 && !loading && (
        <div className="flex flex-col items-center justify-center py-16">
          <div className="bg-gray-100 rounded-full p-3 mb-3">
            <Store className="w-6 h-6 text-gray-400" />
          </div>
          <p className="text-sm text-gray-500">Nenhum lojista encontrado</p>
        </div>
      )}
    </div>
  );
};
